import { getModule } from '@/hooks/useRouteScope';
import { GROUPS, PREFIX } from '@/service/apiConf';
import { deleteRes, get, patchJsonPatch, postJSON, putJSON } from '@/service/request';

import type {
  ModelSubscriptionItem,
  ModelSubscriptionListResult,
  ModelSubscriptionPayload,
  ModelSubscriptionQuotasResult,
  ModelSubscriptionStrategyItem,
  ModelSubscriptionUsageStatsParams,
  ModelSubscriptionUsageStatsResult,
  ModelSubscriptionUsageSummaryResult,
} from './types';

// cspell:ignore LLMGW llmgwmodelbinding llmgwmodelbindings

/** 根据当前路由模式返回模型订阅所属的 LLMGW 接口前缀。 */
const getLlmGwBase = () => {
  /** `console` 表示租户模式，此时接口需要自动携带 workspace 前缀。 */
  const isWorkspaceScope = getModule() === 'console';
  return isWorkspaceScope ? PREFIX.vapis_dc_ws(GROUPS.llmgw) : PREFIX.vapis_dc(GROUPS.llmgw);
};

export const UrlMap = {
  list: () => `${getLlmGwBase()}/llmgwmodelbindings`,
  // 详情、删除
  common: (name: string) => `${getLlmGwBase()}/llmgwmodelbindings/${encodeURIComponent(name)}`,
  // 更新
  updateSubscription: (name: string) => `${getLlmGwBase()}/llmgwmodelbindings/${encodeURIComponent(name)}`,
  // 创建
  createSubscription: () => `${getLlmGwBase()}/llmgwmodelbindings`,
  // 模型厂商策略沿用路由作用域，数据中心下按 query 中的 workspaces 筛选。
  modelStrategies: () => `${getLlmGwBase()}/llmgwstrategies`,
  // 租户空间下，详情页的用量、监控、事件接口需要沿用 BASE，确保请求前缀带上 workspace 信息。
  modelTokenUsage: (llmgwmodelbinding: string) => `${getLlmGwBase()}/llmgwmodelbindings/${llmgwmodelbinding}/quotas`,
  // 重置/充值额度
  rechargeQuota: (llmgwmodelbinding: string, counterName: string) =>
    `${getLlmGwBase()}/llmgwmodelbindings/${llmgwmodelbinding}/quotas/${counterName}`,
  // 概览接口
  summary: (llmgwmodelbinding: string) => `${getLlmGwBase()}/llmgwmodelbindings/${llmgwmodelbinding}/usage-summary`,
  // 事件接口，保留给未挂载的旧事件组件编译引用。
  modelEvents: (llmgwmodelbinding: string) => `${getLlmGwBase()}/llmgwmodelbindings/${llmgwmodelbinding}/events`,
  // 监控接口
  monitor: (llmgwmodelbinding: string) => `${getLlmGwBase()}/llmgwmodelbindings/${llmgwmodelbinding}/usage-stats`,
  // 获取一个自然日的监控数据
  monitor1d: (llmgwmodelbinding: string) => `${getLlmGwBase()}/llmgwmodelbindings/${llmgwmodelbinding}/usage-stats-1d`,
};

export function getList(params?: Record<string, any>) {
  return get<ModelSubscriptionListResult>(
    UrlMap.list(),
    {
      page: 1,
      limit: 10,
      ...params,
    },
    { flag: true },
  );
}

export function getDetail(name: string) {
  return get<ModelSubscriptionItem | { data?: ModelSubscriptionItem }>(UrlMap.common(name));
}

export function update(name: string, payload: ModelSubscriptionPayload) {
  return putJSON<ModelSubscriptionPayload>(UrlMap.updateSubscription(name), payload);
}

export function remove(name: string) {
  return deleteRes(UrlMap.common(name));
}

export function create(payload: ModelSubscriptionPayload) {
  return postJSON<ModelSubscriptionPayload>(UrlMap.createSubscription(), payload);
}

/** 获取全部模型厂商策略，创建订阅时用于选择 strategyName */
export function getModelStrategies(params?: Record<string, any>) {
  /** 表单 workspace 映射为接口的 workspaces 查询参数，不再拼入数据中心路径。 */
  const { workspace, ...restParams } = params || {};
  return get<{ items: ModelSubscriptionStrategyItem[] }>(
    UrlMap.modelStrategies(),
    {
      page: 1,
      page_size: 10000,
      workspaces: workspace,
      ...restParams,
    },
    { flag: true },
  );
}

/** 获取订阅事件列表，兼容旧事件组件编译引用。 */
export function getModelEvents(id: string) {
  return get<Record<string, any>>(UrlMap.modelEvents(id));
}

export function getMonitorData(id: string, params: Record<string, any>) {
  return get<Record<string, any>>(UrlMap.monitor(id), params);
}

export function getModelTokenUsage(id: string) {
  return get<Record<string, any>>(UrlMap.modelTokenUsage(id));
}

export function getModel1dData(id: string, params?: Omit<ModelSubscriptionUsageStatsParams, 'step'>) {
  return get<Record<string, any>>(UrlMap.monitor1d(id), params);
}

/** 获取模型订阅用量概览。 */
export function getUsageSummary(id: string) {
  return get<ModelSubscriptionUsageSummaryResult>(UrlMap.summary(id));
}

/** 获取模型订阅配额用量。 */
export function getModelQuotas(id: string) {
  return get<ModelSubscriptionQuotasResult>(UrlMap.modelTokenUsage(id));
}

/** 获取模型订阅分钟级用量趋势。 */
export function getUsageStats(id: string, params: ModelSubscriptionUsageStatsParams) {
  return get<ModelSubscriptionUsageStatsResult>(UrlMap.monitor(id), params);
}

/** 获取模型订阅按天聚合的用量趋势。 */
export function getUsageStats1d(id: string, params?: Omit<ModelSubscriptionUsageStatsParams, 'step'>) {
  return get<ModelSubscriptionUsageStatsResult>(UrlMap.monitor1d(id), params);
}

// 重置额度
export function resetQuota(llmgwmodelbinding: string, counterName = 'billing.total_tokens') {
  return postJSON(UrlMap.rechargeQuota(llmgwmodelbinding, counterName));
}

// 充值额度
export function rechargeQuota(llmgwmodelbinding: string, counterName = 'billing.total_tokens', payload: { amount: number }) {
  return patchJsonPatch(UrlMap.rechargeQuota(llmgwmodelbinding, counterName), payload);
}
